import { useState, useCallback } from 'react';
import { GuideStep } from './types';

export interface UseGuideOverlayOptions {
  /** 引导步骤数组 */
  steps: GuideStep[];
  /** 是否自动开始 */
  autoStart?: boolean;
  /** 完成后的回调 */
  onComplete?: () => void;
}

export interface UseGuideOverlayReturn {
  /** 是否显示引导 */
  visible: boolean;
  /** 当前步骤索引 */
  currentStep: number;
  /** 开始引导 */
  start: () => void;
  /** 停止引导 */
  stop: () => void;
  /** 下一步 */
  next: () => void;
  /** 上一步 */
  prev: () => void;
  /** 跳转到指定步骤 */
  goToStep: (step: number) => void;
  /** 重置引导 */
  reset: () => void;
}

export const useGuideOverlay = ({
  steps,
  autoStart = false,
  onComplete,
}: UseGuideOverlayOptions): UseGuideOverlayReturn => {
  const [visible, setVisible] = useState(autoStart);
  const [currentStep, setCurrentStep] = useState(0);

  const start = useCallback(() => {
    setVisible(true);
    setCurrentStep(0);
  }, []);

  const stop = useCallback(() => {
    setVisible(false);
  }, []);

  const next = useCallback(() => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setVisible(false);
      onComplete?.();
    }
  }, [currentStep, steps.length, onComplete]);

  const prev = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const goToStep = useCallback(
    (step: number) => {
      if (step >= 0 && step < steps.length) {
        setCurrentStep(step);
      }
    },
    [steps.length]
  );

  const reset = useCallback(() => {
    setCurrentStep(0);
    setVisible(false);
  }, []);

  return {
    visible,
    currentStep,
    start,
    stop,
    next,
    prev,
    goToStep,
    reset,
  };
};
